import React from "react";
import {Box, Container, Divider, Typography} from "@mui/material";
import Navigation from "../../components/Navigation";
import Footer from "../../components/Footer";
import {LinearGradient} from "../../components/Icons/LinearGradient";
import theme from "../../components/styles/theme/theme";

const glossaryTerms = [
  {
    term: "Multi-regional input-output (MRIO) modelling",
    definition:
      "The methodology used to calculate the indirect and induced impacts displayed in the dashboard. In the MRIO model, spending by the Coca-Cola system is used to estimate the total economic output in a congressional district related to the system. The model also uses inter-district trade flow data for 528 commodities to estimate the amount of output generated by sectors in other states necessary to meet that initial demand in the origin district. This is repeated for many iterations across 528 sectors and 435 congressional districts, DC, and Puerto Rico. More information on MRIO models, methodology, and the data used is available on the external dashboard\u2019s methodology page and on the IMPLAN website.",
  },
  {
    term: "Direct jobs",
    definition:
      "In this context, jobs created by the Coca-Cola system (The Coca-Cola Company and its independent bottling partners).",
  },
  {
    term: "Indirect jobs",
    definition:
      "Jobs supported in the value chain through the production and sale of Coca-Cola products (ex. Someone who works at a factory that produces aluminum cans).",
  },
  {
    term: "Induced jobs",
    definition:
      "Jobs supported by the personal respending of salaries of both direct and indirect workers.",
  },
  {
    term: "Trade jobs",
    definition:
      "Positions at retail stores such as convenience stores, supermarkets, gas stations, and any other retailers that sell Coca-Cola products.",
  },
  {
    term: "Service jobs",
    definition: "Positions at bars, restaurants, hotels, etc.",
  },
  {
    term: "Gross Domestic Product (GDP)",
    definition:
      "A monetary measure of the value of final goods and services produced in the economy; it is a comprehensive measure of economic activity for a country.",
  },
  {
    term: "Value added",
    definition:
      "The sum of salaries, taxes, and profits, contributing to the country\u2019s Gross Domestic Product (GDP). The definition of GDP is gross value added (GVA) + net taxes on products (mostly sales tax). Because this analysis covers both the value added of the system and the sales tax collected, this total number is very close to GDP. Therefore, we report value added as equivalent to the system\u2019s GDP contribution in this analysis.",
  },
  {
    term: "Upstream",
    definition:
      "The upstream side of the value chain refers to the goods and services the Coca-Cola system procures in order to make the product. This includes their suppliers and their suppliers\u2019 suppliers. For example, a bottler\u2019s upstream impacts include the money it spent on procuring aluminum cans, as well as the money the can producer spent on the raw material used to produce those cans.",
  },
  {
    term: "Downstream",
    definition:
      "The downstream side of the value chain refers to the sale of the product. This includes trade outlets, such as restaurants and stores, and trade partners\u2019 suppliers, such as a restaurant procuring cups or chairs.",
  },
];

const GlossaryPage: React.FC = () => {
  return (
    <>
      <Navigation />
      <LinearGradient />
      <Container maxWidth="lg" sx={{padding: "40px 24px 60px"}}>
        <Typography
          variant="h4"
          component="h1"
          fontWeight={600}
          color={theme.palette.secondary.main}
          marginBottom="8px"
        >
          Glossary of Terms
        </Typography>
        <Typography
          variant="body1"
          color={theme.palette.primary["600"]}
          marginBottom="32px"
        >
          for Coca-Cola&apos;s Economic Impact Study Dashboard
        </Typography>

        {glossaryTerms.map((item, index) => (
          <Box key={index} sx={{marginBottom: "24px"}}>
            <Typography
              variant="h6"
              component="h2"
              fontWeight={600}
              color={theme.palette.component["900"]}
              marginBottom="8px"
            >
              {item.term}
            </Typography>
            <Typography
              variant="body1"
              color={theme.palette.primary["600"]}
              lineHeight="1.6"
            >
              {item.definition}
            </Typography>
            {index < glossaryTerms.length - 1 && (
              <Divider sx={{marginTop: "24px"}} />
            )}
          </Box>
        ))}
      </Container>
      <Footer />
    </>
  );
};

export default GlossaryPage;
